/**
 * Footer Component
 * ================
 * Site footer with links, social icons, and copyright.
 * Stays at bottom of page with proper layout.
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, Linkedin, Twitter, Facebook, ArrowUp } from 'lucide-react';

/**
 * Footer Component
 * Site-wide footer with navigation and contact info.
 * 
 * @returns {JSX.Element} Footer component
 */
export function Footer() {
  const currentYear = new Date().getFullYear();
  
  // Scroll to top handler
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  
  // Footer link sections
  const footerLinks = {
    product: [
      { label: 'Features', path: '/features' },
      { label: 'Integrations', path: '/integrations' },
      { label: 'How It Works', path: '/how-it-works' },
      { label: 'Results', path: '/results' },
    ],
    company: [
      { label: 'About DME', path: 'https://dmedelivers.com/about-us/', external: true },
      { label: 'Case Studies', path: 'https://dmedelivers.com/case-study/', external: true },
      { label: 'Careers', path: 'https://dmedelivers.com/careers/', external: true },
      { label: 'Contact', path: '/contact' },
    ],
    resources: [
      { label: 'Demo', path: '/login' },
      { label: 'FAQ', path: '/faq' },
      { label: 'Support', path: 'mailto:support@dmedelivers.com' },
      { label: 'Privacy Policy', path: 'https://dmedelivers.com/privacy-policy/', external: true },
    ],
    legal: [
      { label: 'Terms of Service', path: 'https://dmedelivers.com/terms-of-service/', external: true },
      { label: 'Cookie Policy', path: '#' },
      { label: 'GDPR', path: '#' },
    ],
  };

  // Contact info
  const contactInfo = {
    email: 'info@dmedelivers.com',
    phone: '(386) 271-3000',
    address: {
      street: '1830 Holsonback Drive',
      city: 'Daytona Beach',
      state: 'FL',
      zip: '32117',
    },
    social: {
      linkedin: 'https://linkedin.com',
      twitter: 'https://twitter.com',
      facebook: 'https://facebook.com',
    },
  };
  
  return (
    <footer className="bg-dme-blue-900 text-white w-full">
      {/* Main Footer */}
      <div className="container-dme py-16 lg:py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-10 lg:gap-8">
          {/* Brand Column */}
          <div className="lg:col-span-2">
            <Link to="/" className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-dme-orange to-dme-orange-dark flex items-center justify-center">
                <span className="text-white font-bold text-xl">D</span>
              </div>
              <div>
                <span className="font-heading font-bold text-xl block">DME Delivers</span>
                <span className="text-dme-orange text-sm">Commitment Predictor Hub</span>
              </div>
            </Link>
            <p className="text-dme-gray-300 mb-6 max-w-sm">
              AI-powered enrollment prediction with 91.8% accuracy. 
              Know which students will commit before they do.
            </p>
            
            {/* Contact Info */}
            <div className="space-y-3">
              <a 
                href={`mailto:${contactInfo.email}`}
                className="flex items-center gap-3 text-dme-gray-300 hover:text-dme-orange transition-colors"
              >
                <Mail className="w-5 h-5" />
                <span>{contactInfo.email}</span>
              </a>
              <a 
                href={`tel:${contactInfo.phone}`}
                className="flex items-center gap-3 text-dme-gray-300 hover:text-dme-orange transition-colors"
              >
                <Phone className="w-5 h-5" />
                <span>{contactInfo.phone}</span>
              </a>
              <div className="flex items-start gap-3 text-dme-gray-300">
                <MapPin className="w-5 h-5 flex-shrink-0 mt-0.5" />
                <span>
                  {contactInfo.address.street}<br />
                  {contactInfo.address.city}, {contactInfo.address.state} {contactInfo.address.zip}
                </span>
              </div>
            </div>
          </div>
          
          {/* Links Columns */}
          <div>
            <h4 className="font-heading font-semibold text-lg mb-4 text-white">Product</h4>
            <ul className="space-y-3">
              {footerLinks.product.map((link) => (
                <li key={link.label}>
                  <Link 
                    to={link.path}
                    className="text-dme-gray-300 hover:text-dme-orange transition-colors"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          
          <div>
            <h4 className="font-heading font-semibold text-lg mb-4 text-white">Company</h4>
            <ul className="space-y-3">
              {footerLinks.company.map((link) => (
                <li key={link.label}>
                  {link.external ? (
                    <a 
                      href={link.path}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-dme-gray-300 hover:text-dme-orange transition-colors"
                    >
                      {link.label}
                    </a>
                  ) : (
                    <Link 
                      to={link.path}
                      className="text-dme-gray-300 hover:text-dme-orange transition-colors"
                    >
                      {link.label}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </div>
          
          <div>
            <h4 className="font-heading font-semibold text-lg mb-4 text-white">Resources</h4>
            <ul className="space-y-3">
              {footerLinks.resources.map((link) => (
                <li key={link.label}>
                  {link.external ? (
                    <a 
                      href={link.path}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-dme-gray-300 hover:text-dme-orange transition-colors"
                    >
                      {link.label}
                    </a>
                  ) : (
                    <Link 
                      to={link.path}
                      className="text-dme-gray-300 hover:text-dme-orange transition-colors"
                    >
                      {link.label}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </div>
          
          <div>
            <h4 className="font-heading font-semibold text-lg mb-4 text-white">Legal</h4>
            <ul className="space-y-3">
              {footerLinks.legal.map((link) => (
                <li key={link.label}>
                  {link.external ? (
                    <a 
                      href={link.path}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-dme-gray-300 hover:text-dme-orange transition-colors"
                    >
                      {link.label}
                    </a>
                  ) : (
                    <Link 
                      to={link.path}
                      className="text-dme-gray-300 hover:text-dme-orange transition-colors"
                    >
                      {link.label}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
      
      {/* Bottom Bar */}
      <div className="border-t border-dme-blue-800">
        <div className="container-dme py-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            {/* Copyright */}
            <p className="text-dme-gray-400 text-sm text-center md:text-left">
              © {currentYear} DME Delivers. All rights reserved.
            </p>
            
            {/* Social Links */}
            <div className="flex items-center gap-4">
              <a 
                href={contactInfo.social.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-dme-blue-800 flex items-center justify-center text-dme-gray-300 hover:bg-dme-orange hover:text-white transition-all"
                aria-label="LinkedIn"
              >
                <Linkedin className="w-5 h-5" />
              </a>
              <a 
                href={contactInfo.social.twitter}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-dme-blue-800 flex items-center justify-center text-dme-gray-300 hover:bg-dme-orange hover:text-white transition-all"
                aria-label="Twitter"
              >
                <Twitter className="w-5 h-5" />
              </a>
              <a 
                href={contactInfo.social.facebook}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-dme-blue-800 flex items-center justify-center text-dme-gray-300 hover:bg-dme-orange hover:text-white transition-all"
                aria-label="Facebook"
              >
                <Facebook className="w-5 h-5" />
              </a>
            </div>
            
            {/* Back to Top */}
            <button
              onClick={scrollToTop}
              className="flex items-center gap-2 text-dme-gray-400 hover:text-dme-orange transition-colors text-sm"
            >
              <span>Back to top</span>
              <ArrowUp className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
}

export default Footer;
